import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import RichTextEditor from '../RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
    title: '',
    slug: '',
    content: '',
    show_in_nav: false,
    nav_scope: 'both',
    nav_order: 100,
    is_published: true
};

export default function CustomPagesPanel() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/pages`, { withCredentials: true });
            setPages(data);
        } catch (err) {
            toast.error('Failed to load pages');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (page) => {
        setEditingId(page.id);
        setForm({
            title: page.title,
            slug: page.slug,
            content: page.content,
            show_in_nav: !!page.show_in_nav,
            nav_scope: page.nav_scope || 'both',
            nav_order: page.nav_order ?? 100,
            is_published: page.is_published !== false
        });
        setDialogOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingId) {
                await axios.put(`${API_URL}/api/pages/${editingId}`, form, { withCredentials: true });
                toast.success('Page updated');
            } else {
                await axios.post(`${API_URL}/api/pages`, form, { withCredentials: true });
                toast.success('Page created');
            }
            setDialogOpen(false);
            load();
            // Force navbar to refetch
            window.dispatchEvent(new CustomEvent('pages-changed'));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save page');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this page? This cannot be undone.')) return;
        try {
            await axios.delete(`${API_URL}/api/pages/${id}`, { withCredentials: true });
            toast.success('Page deleted');
            load();
            window.dispatchEvent(new CustomEvent('pages-changed'));
        } catch (err) {
            toast.error('Failed to delete page');
        }
    };

    return (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8" data-testid="custom-pages-panel">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <p className="overline mb-2">Content</p>
                    <h2 className="font-serif text-3xl text-[#1A1A2E]">Custom Pages</h2>
                    <p className="text-sm text-[#4A4A68] mt-2">
                        Add custom pages (About, Mission, FAQ, Terms…) and optionally show them in the navbar.
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
                    <DialogTrigger asChild>
                        <button className="btn-primary flex items-center gap-2" onClick={openCreate} data-testid="create-page-btn">
                            <Plus className="w-4 h-4" /> Add Page
                        </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                {editingId ? 'Edit Page' : 'Create Page'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 mt-4">
                            <div>
                                <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Title *</label>
                                <input
                                    type="text"
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="w-full"
                                    data-testid="page-title"
                                />
                            </div>
                            <div>
                                <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Slug (URL) *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="about-us"
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    className="w-full"
                                    data-testid="page-slug"
                                />
                                <p className="text-xs text-[#4A4A68] mt-1">Will be visible at /p/{form.slug || 'your-slug'}</p>
                            </div>
                            <div>
                                <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                                    Content
                                </label>
                                <RichTextEditor
                                    value={form.content}
                                    onChange={(html) => setForm({ ...form, content: html })}
                                    placeholder="Write your page content here…"
                                    minHeight="320px"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.show_in_nav}
                                        onChange={(e) => setForm({ ...form, show_in_nav: e.target.checked })}
                                        data-testid="page-show-in-nav"
                                    />
                                    <span className="text-sm text-[#1A1A2E]">Show in navigation</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.is_published}
                                        onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                                        data-testid="page-published"
                                    />
                                    <span className="text-sm text-[#1A1A2E]">Published</span>
                                </label>
                            </div>
                            {form.show_in_nav && (
                                <div>
                                    <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                                        Visible in which navbar?
                                    </label>
                                    <select
                                        value={form.nav_scope || 'both'}
                                        onChange={(e) => setForm({ ...form, nav_scope: e.target.value })}
                                        className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                        data-testid="page-nav-scope"
                                    >
                                        <option value="both">Both — public and members</option>
                                        <option value="public">Public only — only logged-out visitors see it</option>
                                        <option value="members">Members only — only authenticated users see it</option>
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">Nav Order</label>
                                <input
                                    type="number"
                                    value={form.nav_order}
                                    onChange={(e) => setForm({ ...form, nav_order: parseInt(e.target.value) || 100 })}
                                    className="w-32"
                                    data-testid="page-nav-order"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn-primary w-full flex items-center justify-center gap-2"
                                data-testid="save-page-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {saving ? 'Saving…' : (editingId ? 'Update Page' : 'Create Page')}
                            </button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" /></div>
            ) : pages.length === 0 ? (
                <div className="text-center py-12 text-[#4A4A68]">
                    <p>No custom pages yet. Create your first page to get started.</p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="pages-list">
                    {pages.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-4 border border-[#E5E0D8] rounded-sm hover:border-[#5B2D8E] transition" data-testid={`page-row-${p.slug}`}>
                            <div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <p className="font-serif text-lg text-[#1A1A2E]">{p.title}</p>
                                    <code className="text-xs bg-[#F2EBE5] px-2 py-1 rounded">/p/{p.slug}</code>
                                    {p.show_in_nav && (
                                        <span className="text-xs px-2 py-1 bg-[#5B2D8E] text-white rounded-sm">
                                            In Nav ({p.nav_scope || 'both'})
                                        </span>
                                    )}
                                    {!p.is_published && (
                                        <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-sm">Draft</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <a
                                    href={`/p/${p.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-2 hover:bg-[#F2EBE5] rounded"
                                    title="View"
                                >
                                    {p.is_published ? <Eye className="w-4 h-4 text-[#4A4A68]" /> : <EyeOff className="w-4 h-4 text-[#4A4A68]" />}
                                </a>
                                <button onClick={() => openEdit(p)} className="p-2 hover:bg-[#F2EBE5] rounded" data-testid={`edit-page-${p.slug}`}>
                                    <Edit2 className="w-4 h-4 text-[#4A4A68]" />
                                </button>
                                <button onClick={() => remove(p.id)} className="p-2 hover:bg-red-50 rounded" data-testid={`delete-page-${p.slug}`}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
